import { normalizeGenericResourceDetail } from '../../genericResourceAdapter'
import { parseExtaObject } from '../genericResourceExtaUtils'
import { summarizeZipPackageSpec } from '../genericResourceZipPackageSpec'

import {
  hasSideViewFromRefMeta,
  readVideoDurationSeconds,
  readVideoSideDurationSeconds,
} from './workoutActionBindingCalculator'
import { normalizeWorkoutDifficulty } from './workoutFormConstants'

const WORKOUT_ZONE_BY_ASSET_TYPE = {
  workout_action: 'actions',
  workout_music: 'music',
  workout_voice: 'voices',
}

/** 将媒体时长格式化为运营可读文案（支持 1 位小数）。 */
export function formatMediaDurationSeconds(value) {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return '—'
  }
  const rounded = Math.round(parsed * 10) / 10
  return `${rounded}s`
}

/**
 * 动作主/侧时长展示：`主 5s  侧：5s`；无侧视角时侧为 —。
 * 用于挑选弹窗 duration 列与合集动作区摘要。
 */
export function formatActionMainSideDurationLabel(refMeta) {
  const meta = refMeta && typeof refMeta === 'object' ? refMeta : {}
  const mainLabel = formatMediaDurationSeconds(meta.videoDurationSeconds)
  const hasSide = hasSideViewFromRefMeta(meta)
  const sideLabel = hasSide
    ? formatMediaDurationSeconds(meta.videoSideDurationSeconds)
    : '—'
  return `主 ${mainLabel}  侧：${sideLabel}`
}

/** @deprecated 使用 formatMediaDurationSeconds */
export function formatVideoDurationSeconds(value) {
  return formatMediaDurationSeconds(value)
}

export function workoutAssetTypeToZoneId(assetType) {
  return WORKOUT_ZONE_BY_ASSET_TYPE[String(assetType || '')] || ''
}

/**
 * 编辑页 ref_meta 是否需重新拉取：已有对象但缺关键字段（动作 V / 音乐时长 / 动作 tags）时仍要 refresh。
 */
export function needsBindingRefMetaRefresh(refMeta, zoneId) {
  if (!refMeta || typeof refMeta !== 'object') {
    return true
  }
  if (zoneId === 'actions') {
    // tagTitles / hasSideView 是后续加的 CMS 展示字段；缺属性说明是旧快照，需重拉详情
    if (!Object.prototype.hasOwnProperty.call(refMeta, 'tagTitles')) {
      return true
    }
    if (!Object.prototype.hasOwnProperty.call(refMeta, 'hasSideView')) {
      return true
    }
    return readVideoDurationSeconds(refMeta) == null
  }
  if (zoneId === 'music') {
    const raw = refMeta.audioDurationSeconds
    const parsed = Number.parseFloat(raw)
    return !Number.isFinite(parsed) || parsed <= 0
  }
  return false
}

export function isWorkoutPickerAssetType(assetType) {
  return Boolean(workoutAssetTypeToZoneId(assetType))
}

function readJsonConfig(normalized) {
  return normalized.jsonConfig || normalized.json_config || {}
}

/**
 * 从详情 VO 提取 Tag 展示名（name 优先，兼容 title）。
 * @returns {string[]}
 */
export function extractTagTitlesFromDetail(detail) {
  const raw =
    (detail && detail.tagVOList) ||
    (detail && detail.tag_vo_list) ||
    []
  if (!Array.isArray(raw)) {
    return []
  }
  return raw
    .map((tag) => {
      if (!tag || typeof tag !== 'object') {
        return ''
      }
      return String(tag.name || tag.title || '').trim()
    })
    .filter(Boolean)
}

function formatTagTitlesLabel(tagTitles) {
  if (!Array.isArray(tagTitles) || !tagTitles.length) {
    return '—'
  }
  return tagTitles.join(' · ')
}

/** 优先 met；旧动作资源仅有 calories 时回显为 MET（CMS 展示兼容）。 */
function resolveMetFromExta(exta) {
  const data = exta || {}
  const raw = data.met != null ? data.met : data.calories
  if (raw == null || raw === '') {
    return null
  }
  const parsed = Number.parseFloat(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return Math.round(parsed * 10) / 10
}

/**
 * 从详情 VO 提取合集绑定条目的只读快照（CMS 展示用，App 可忽略 ref_meta）。
 */
export function buildBindingRefMeta(assetType, detail) {
  const normalized = normalizeGenericResourceDetail(detail) || {}
  const type = String(assetType || normalized.assetType || '')
  const jsonConfig = readJsonConfig(normalized)
  const exta = parseExtaObject(jsonConfig.exta) || {}
  const url = jsonConfig.url != null ? String(jsonConfig.url).trim() : ''
  // Tag 来自详情 VO（列表接口通常不带）；合集 CMS 只展示，不 enrichment 回 App
  const tagTitles = extractTagTitlesFromDetail(detail)

  const base = {
    assetType: type,
    displayName: exta.display_name || normalized.resourceName || '',
    zipUploaded: Boolean(url),
    zipFileName: url ? url.split('/').pop() : '',
    thumbUrl: normalized.thumbUrl || '',
  }

  if (type === 'workout_action') {
    const videoSideDurationSeconds = readVideoSideDurationSeconds({
      video_side_duration_seconds: exta.video_side_duration_seconds,
    })
    const sideUrl =
      exta.video_file_side != null ? String(exta.video_file_side).trim() : ''
    // 仅有侧视角 CDN 才算双视角，避免无文件却残留侧时长时误算总时长
    const hasSideView = Boolean(sideUrl)
    return {
      ...base,
      difficulty: normalizeWorkoutDifficulty(exta.difficulty),
      videoLoop: typeof exta.video_loop === 'boolean' ? exta.video_loop : null,
      met: resolveMetFromExta(exta),
      videoDurationSeconds: readVideoDurationSeconds({
        video_duration_seconds: exta.video_duration_seconds,
      }),
      videoSideDurationSeconds,
      hasSideView,
      tagTitles,
    }
  }
  if (type === 'workout_music') {
    return {
      ...base,
      loopable: typeof exta.loopable === 'boolean' ? exta.loopable : null,
      audioDurationSeconds:
        exta.audio_duration_seconds != null ? exta.audio_duration_seconds : null,
    }
  }
  if (type === 'workout_voice') {
    return {
      ...base,
      gender: exta.gender || '',
      language: exta.language || '',
    }
  }
  return base
}

/**
 * 合集绑定卡片上的 ref_meta 摘要行（按区块只展示关键识别信息）。
 */
export function formatBindingRefMetaLines(refMeta, zoneId) {
  const meta = refMeta && typeof refMeta === 'object' ? refMeta : {}
  if (zoneId === 'actions') {
    // 仅 2×2 识别字段；tags 由绑定区单独一行铺满右侧空白
    const durationLabel = formatActionMainSideDurationLabel(meta)
    const durationMuted =
      meta.videoDurationSeconds == null || Number(meta.videoDurationSeconds) <= 0
    return [
      { key: 'difficulty', value: meta.difficulty || '—', labelTone: 'short' },
      {
        key: 'duration',
        value: durationLabel,
        muted: durationMuted,
        labelTone: 'long',
      },
      {
        key: 'loop',
        value: meta.videoLoop != null ? (meta.videoLoop ? 'true' : 'false') : '—',
        muted: meta.videoLoop == null,
        labelTone: 'short',
      },
      {
        key: 'MET',
        value: meta.met != null ? String(meta.met) : '—',
        muted: meta.met == null || Number(meta.met) <= 0,
        labelTone: 'short',
      },
    ]
  }
  if (zoneId === 'voices') {
    return [
      { key: 'gender', value: meta.gender || '—' },
      { key: 'language', value: meta.language || '—' },
    ]
  }
  if (zoneId === 'music') {
    return [
      {
        key: 'audioDurationSeconds',
        value: formatMediaDurationSeconds(meta.audioDurationSeconds),
        muted: meta.audioDurationSeconds == null || Number(meta.audioDurationSeconds) <= 0,
      },
      {
        key: 'loopable',
        value: meta.loopable != null ? (meta.loopable ? 'true' : 'false') : '—',
        muted: meta.loopable == null,
      },
    ]
  }
  return []
}

/** 合集挑选弹窗：左侧紧凑固定宽，右侧 tags 换行吃余量。 */
export const WORKOUT_PICKER_COLUMN_WIDTHS = {
  selection: 40,
  preview: 56,
  resourceName: 140,
  status: 80,
}

/** 每种 workout 类型的 exta 独立列（固定 width，单元格内居中）。 */
export function getWorkoutPickerExtaColumns(assetType) {
  const type = String(assetType || '')
  if (type === 'workout_action') {
    return [
      { key: 'difficulty', label: 'difficulty', width: 110 },
      { key: 'met', label: 'MET', width: 64 },
      { key: 'videoLoop', label: 'loop', width: 80, renderAs: 'boolPill' },
      { key: 'hasSideView', label: '是否有侧视角', width: 112 },
      { key: 'videoDurationSeconds', label: 'duration', width: 148 },
      // tags 可换行；用 chip 展示，minWidth 吃剩余宽
      { key: 'tags', label: 'tags', minWidth: 200, renderAs: 'tagChips' },
    ]
  }
  if (type === 'workout_music') {
    return [
      { key: 'loopable', label: 'loopable', width: 96, renderAs: 'boolPill' },
      { key: 'audioDurationSeconds', label: 'duration', width: 88 },
    ]
  }
  if (type === 'workout_voice') {
    return [
      { key: 'gender', label: 'gender', width: 88 },
      { key: 'language', label: 'language', width: 96 },
    ]
  }
  return []
}

/** 从 ref_meta 提取挑选弹窗各列单元格值。 */
export function buildWorkoutPickerExtaMap(assetType, refMeta) {
  const meta = refMeta && typeof refMeta === 'object' ? refMeta : {}
  if (assetType === 'workout_action') {
    const tagTitles = Array.isArray(meta.tagTitles) ? meta.tagTitles.filter(Boolean) : []
    const hasSide = hasSideViewFromRefMeta(meta)
    return {
      difficulty: normalizeWorkoutDifficulty(meta.difficulty) || '—',
      met: meta.met != null ? String(meta.met) : '—',
      videoLoop: meta.videoLoop != null ? (meta.videoLoop ? 'true' : 'false') : '—',
      hasSideView: hasSide ? '有' : '无',
      videoDurationSeconds: formatActionMainSideDurationLabel(meta),
      tags: formatTagTitlesLabel(tagTitles),
      tagTitles,
    }
  }
  if (assetType === 'workout_music') {
    return {
      loopable: meta.loopable != null ? (meta.loopable ? 'true' : 'false') : '—',
      audioDurationSeconds: formatMediaDurationSeconds(meta.audioDurationSeconds),
    }
  }
  if (assetType === 'workout_voice') {
    return {
      gender: meta.gender || '—',
      language: meta.language || '—',
    }
  }
  return {}
}

export function buildZipPackageStatus(assetType, jsonConfig) {
  const type = String(assetType || '')
  const url = jsonConfig && jsonConfig.url != null ? String(jsonConfig.url).trim() : ''
  const uploaded = Boolean(url)
  return {
    uploaded,
    statusText: uploaded ? '已上传' : '未上传',
    fileName: uploaded ? url.split('/').pop() : '—',
    specHint: summarizeZipPackageSpec(type),
  }
}
